import React from 'react';
import styled from 'styled-components';
import Text from '@service-victoria/component-library/components/Text';
import Page from '@service-victoria/component-library/components/Page';
import { Button } from '@service-victoria/component-library/components/Button';
import Accordion from '@service-victoria/component-library/components/Accordion';
import Tabs, { TabItem } from '@service-victoria/component-library/components/Tabs';
import List from '@service-victoria/component-library/components/List';
import Spinner from '@service-victoria/component-library/components/Icons/Spinner';
import { useHistory } from 'react-router-dom';
import { constants, logger } from 'utils';
import bannerImg from 'assets/melbourne-city-at-night.png';
import DJPRLogo from 'assets/djpr.jpg';
import * as Styled from './styled';
import { useWindowSize } from 'hooks';
import { app as appContext } from 'context';
import Unavailable from './Unavailable';
import TooEarly from './TooEarly';
import FAQs from './FAQs';
import useAvailable from 'hooks/useAvailable';
import { Availability } from 'interfaces/common';
import { AddAvailability } from 'interfaces/store';

function homeContent(isAvailable: Availability | undefined, updateAvailability: AddAvailability) {
  const mainContent =
    isAvailable === 'TOO_EARLY' ? (
      <TooEarly updateAvailability={updateAvailability} />
    ) : (
      <React.Fragment>
        <Text.Heading.H3 fontWeight={600}>Program overview</Text.Heading.H3>
        <Text.Body>Melbourne travel voucher applications are now open.</Text.Body>
        <Text.Body>
          These travel vouchers apply to the period of {constants.contentDates.travelPeriodStart} to{' '}
          {constants.contentDates.travelPeriodEnd} 2021.
        </Text.Body>
        <Text.Body>
          Make sure you have your Victorian driver licence, or seniors card or proof-of-age card
          handy. You&apos;ll need it to complete your application.
        </Text.Body>
        <Text.Body>
          If you are a successful applicant, you can submit your claim for a $200 reimbursement
          after you travel.
        </Text.Body>
        <Styled.Spacer24 />
        <Text.Heading.H3 fontWeight={600}>How to apply:</Text.Heading.H3>
        <List
          type='disc'
          items={[
            'You must have stayed at least two consecutive nights in metropolitan Melbourne and spent at least $400 on paid accommodation, plus attractions, experiences or tours.',
            'One voucher per household for the whole scheme.',
            'You must be over 18 and a Victorian resident.',
            `To redeem your voucher, you must keep your receipts and claim online before ${constants.contentDates.claimDealine}.`
          ]}
        />
        <Styled.TextBodyWithInlineLink>
          <Text.Link
            key='privacy-link'
            showExtraLink
            target='_blank'
            value='Read more about the scheme'
            href={constants.externalLinks.metroVoucherSchemeFaqs}
          />{' '}
          and what you can claim.
        </Styled.TextBodyWithInlineLink>
        <Styled.TextBodyWithInlineLink>
          <Text.Heading.H3 fontWeight={600}>Protecting your privacy:</Text.Heading.H3>
          <List
            type='disc'
            items={[
              "We'll temporarily store your info you give us, then pass it onto the Department of Jobs Precincts and Regions to assess your eligibility for the Melbourne travel voucher scheme.",
              <Text.Link
                key='privacy-link'
                showExtraLink
                target='_blank'
                value='Read the full privacy collection notice.'
                href={constants.externalLinks.metroVoucherSchemeCollectionNotice}
              />
            ]}
          />
        </Styled.TextBodyWithInlineLink>
      </React.Fragment>
    );

  const items: TabItem[] = [
    {
      id: 'before-you-start',
      title: 'Before you start',
      component: mainContent
    },
    {
      id: 'faq',
      title: 'FAQs',
      component: <FAQs />
    }
  ];

  return items;
}

const PageWrapper = styled(Page)``;

const Home: React.FC = () => {
  const history = useHistory();
  const windowSize = useWindowSize();
  const { currentState, actions } = appContext.useAppContext();
  const isAvailable = currentState.availability;
  const items = homeContent(isAvailable, actions.updateAvailability);
  // assume true?

  useAvailable();

  return (
    <PageWrapper
      title={constants.Page.title}
      showFooter={true}
      breadCrumbs={constants.breadCrumbs}
      imgUrl={bannerImg}
      loading={false}
      error={undefined}
      opacityBetweenLoading={0}>
      {isAvailable === undefined && (
        <Styled.Loader>
          <Spinner />
        </Styled.Loader>
      )}
      {(isAvailable === 'SUCCESS' || isAvailable === 'TOO_EARLY') && (
        <Styled.Wrapper>
          <Styled.Header>
            <Styled.Description>
              Register for a $200 voucher to spend on accommodation, attractions or tours in
              metropolitan Melbourne
            </Styled.Description>
            <Styled.Line />
            <Styled.Timer>
              <Styled.Icon />
              <Styled.TextHead>
                This takes about
                <span>6 mins</span>
              </Styled.TextHead>
            </Styled.Timer>
          </Styled.Header>

          <Styled.DoubleLine />
          <Styled.Body>
            {windowSize === 'mobile' ? <Accordion items={items} /> : <Tabs items={items} />}
          </Styled.Body>

          {isAvailable === 'SUCCESS' && (
            <Styled.ButtonWrapper>
              <Button
                buttonType='primary'
                onClick={() => {
                  logger.info(
                    `Start Melbourne Travel Voucher transaction ${currentState.transactionID}`,
                    {
                      transactionID: currentState.transactionID
                    }
                  );
                  history.push(constants.Routes.Transaction);
                }}>
                Get started
              </Button>
            </Styled.ButtonWrapper>
          )}

          <Styled.Logos>
            <Styled.VicLogo>
              A program of
              <img src={DJPRLogo} alt='djpr-logo' />
            </Styled.VicLogo>
          </Styled.Logos>
        </Styled.Wrapper>
      )}
      {isAvailable === 'TOO_LATE' && <Unavailable />}
    </PageWrapper>
  );
};

export default Home;
